import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Link, Route, Routes, useLocation } from "react-router-dom";
import "@fontsource/pacifico";
import blogContent from "./Offlinedata/blogsitejson.json";
import docusaurusArticles from "./Offlinedata/docusaurusArticles.json";
import InformativeLoader from "./components/Loader/InformativeLoader";
import "./App.css";
import NavigationHome from "./components/Navigation/NavigationHome";
import HeroModern from "./components/HeroModern/HeroModern";
import Tile from "./components/Tile";
import Profile from "./components/ProfileCard/Profile";
import PdfViewer from "./components/PdfViewer";
import ReadmeViewer from "./components/ReadmeViewer";
import SearchBox from "./components/Searchbox/SearchBox";
import Pagination from "react-js-pagination";

function getInitialTheme() {
  if (typeof window === "undefined") {
    return "dark";
  }

  const savedTheme = window.localStorage.getItem("site-theme");

  if (savedTheme === "light" || savedTheme === "dark") {
    return savedTheme;
  }

  return "dark";
}

const CATEGORY_CONFIG = [
  {
    key: "all",
    path: "/content",
    label: "Content Library",
    heroTitle: "Articles, docs, architecture notes, and AI builds",
    heroDescription:
      "Browse everything I publish in one place, from blog posts and walkthroughs to static docs, reference notes, and technical deep dives.",
  },
  {
    key: "Sharepoint",
    path: "/sharepoint",
    label: "Dynamics & Power Platform",
    heroTitle: "Dynamics, Power Platform, and Microsoft business apps",
    heroDescription:
      "Posts and resources around Dynamics 365, Power Apps, Power Automate, SharePoint, ALM, and enterprise business workflows.",
  },
  {
    key: "Azure",
    path: "/azure",
    label: "Azure, AI & Architecture",
    heroTitle: "Azure architecture, AI engineering, and cloud integration",
    heroDescription:
      "Notes and articles on Azure services, AI patterns, security, identity, integration, and cloud-native architecture work.",
  },
  {
    key: "Docusaurus",
    path: "/docs",
    label: "Architecture Docs",
    heroTitle: "Static docs, architecture guides, and reference notes",
    heroDescription:
      "Docusaurus-based documentation, long-form implementation guides, and reference material published as a static docs site.",
  },
];

const SECTION_ORDER = ["Sharepoint", "Azure", "Docusaurus"];

function getPrimaryUrl(item) {
  return item.PrimaryUrl || item.ExternalUrl || item.PdfUrl?.Url || item.YouTubeUrl || item.GitHubRepo || "";
}

function inferSource(item) {
  if (item.Source) {
    return item.Source;
  }

  const link = item.PrimaryUrl || item.ExternalUrl || item.PdfUrl?.Url || "";

  if ((item.BlogType || "").toLowerCase() === "docusaurus") {
    return "Docusaurus";
  }

  if (link.includes("linkedin.com")) {
    return "LinkedIn";
  }

  if (link.includes("github.com") && link.includes(".md")) {
    return "GitHub Markdown";
  }

  if (link.includes("github.com")) {
    return "GitHub";
  }

  if (item.YouTubeUrl) {
    return "YouTube";
  }

  return "Article";
}

function getCtaLabel(item, source) {
  if (item.CtaLabel) {
    return item.CtaLabel;
  }

  if (source === "Docusaurus") {
    return "Open docs";
  }

  if (source === "LinkedIn") {
    return "Open post";
  }

  if (item.YouTubeUrl && getPrimaryUrl(item) === item.YouTubeUrl) {
    return "Watch now";
  }

  return "Read more";
}

function normalizeTile(item, index) {
  const source = inferSource(item);
  const primaryUrl = getPrimaryUrl(item);

  return {
    ...item,
    ID: item.ID ?? item.Id ?? `${item.BlogType || "content"}-${index}`,
    Title: item.Title || "Untitled post",
    Description: item.Description || "No description added yet.",
    BlogType: item.BlogType || "General",
    Source: source,
    PrimaryUrl: primaryUrl,
    CtaLabel: getCtaLabel(item, source),
    PublishedOn: item.PublishedOn || item.Modified || item.Created || null,
    GitHubRepo: item.GitHubRepo || item.RepositoryUrl || null,
    YouTubeUrl: item.YouTubeUrl || null,
  };
}

function sortTiles(items) {
  return [...items].sort((left, right) => {
    const leftDate = new Date(left.PublishedOn || 0).getTime();
    const rightDate = new Date(right.PublishedOn || 0).getTime();

    if (leftDate !== rightDate) {
      return rightDate - leftDate;
    }

    const numericLeft = Number(left.ID);
    const numericRight = Number(right.ID);

    if (!Number.isNaN(numericLeft) && !Number.isNaN(numericRight)) {
      return numericRight - numericLeft;
    }

    return String(right.ID).localeCompare(String(left.ID));
  });
}

function matchesSearch(tile, searchValue) {
  const search = searchValue.trim().toLowerCase();

  if (!search) {
    return true;
  }

  return [
    tile.Title,
    tile.Description,
    tile.BlogType,
    tile.Source,
    tile.GitHubRepo,
    tile.PrimaryUrl,
  ]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(search));
}

function getTilesByCategory(tiles, categoryKey) {
  if (categoryKey === "all") {
    return tiles;
  }

  return tiles.filter((tile) => tile.BlogType === categoryKey);
}

function getCategoryMeta(pathname) {
  return CATEGORY_CONFIG.find((item) => item.path === pathname) || null;
}

function HomePage({ tiles }) {
  const latestTiles = tiles.slice(0, 6);
  const sections = SECTION_ORDER.map((categoryKey) => ({
    config: CATEGORY_CONFIG.find((item) => item.key === categoryKey),
    tiles: getTilesByCategory(tiles, categoryKey).slice(0, 4),
  })).filter((section) => section.config && section.tiles.length > 0);

  return (
    <div className="content-shell">
      <HeroModern />

      {latestTiles.length > 0 && (
        <section className="tile-section">
          <div className="section-heading">
            <div>
              <p className="section-kicker">Latest</p>
              <h2 className="tile-H2Home">Latest writing, build notes, and field guides</h2>
            </div>
            <Link className="blogLoadMoreHome" to="/content">
              View library
            </Link>
          </div>
          <div className="tiles">
            {latestTiles.map((tile) => (
              <Tile key={tile.ID} tile={tile} />
            ))}
          </div>
        </section>
      )}

      {sections.map((section) => (
        <section className="tile-section" key={section.config.key}>
          <div className="section-heading">
            <div>
              <p className="section-kicker">{section.config.label}</p>
              <h2 className="tile-H2Home">{section.config.heroTitle}</h2>
            </div>
            <Link className="blogLoadMoreHome" to={section.config.path}>
              Open collection
            </Link>
          </div>
          <div className="tiles">
            {section.tiles.map((tile) => (
              <Tile key={tile.ID} tile={tile} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function CollectionPage({ tiles, categoryKey }) {
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 9;
  const categoryTiles = getTilesByCategory(tiles, categoryKey);
  const categoryMeta = CATEGORY_CONFIG.find((item) => item.key === categoryKey) || CATEGORY_CONFIG[0];
  const indexOfLastTile = activePage * itemsPerPage;
  const indexOfFirstTile = indexOfLastTile - itemsPerPage;
  const currentTiles = categoryTiles.slice(indexOfFirstTile, indexOfLastTile);

  useEffect(() => {
    setActivePage(1);
  }, [categoryKey, tiles]);

  return (
    <div className="content-shell">
      <section className="collection-hero">
        <p className="section-kicker">{categoryMeta.label}</p>
        <h1 className="collection-title">{categoryMeta.heroTitle}</h1>
        <p className="collection-copy">{categoryMeta.heroDescription}</p>
      </section>

      {categoryTiles.length === 0 ? (
        <div className="empty-state">
          <h2>No content found yet</h2>
          <p>
            This collection is ready. Add items to the JSON feed and they will show up here
            automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="tiles">
            {currentTiles.map((tile) => (
              <Tile key={tile.ID} tile={tile} />
            ))}
          </div>
          {categoryTiles.length > itemsPerPage && (
            <Pagination
              activePage={activePage}
              itemsCountPerPage={itemsPerPage}
              totalItemsCount={categoryTiles.length}
              pageRangeDisplayed={5}
              onChange={setActivePage}
              itemClass="page-item"
              linkClass="page-link"
              prevPageText="Prev"
              nextPageText="Next"
            />
          )}
        </>
      )}
    </div>
  );
}

function MainContent() {
  const location = useLocation();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const mergedTiles = sortTiles(
      [...blogContent, ...docusaurusArticles].map((item, index) => normalizeTile(item, index))
    );

    setTiles(mergedTiles);
    setLoading(false);
  }, []);

  useEffect(() => {
    setSearchValue("");
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem("site-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  const routeMeta = getCategoryMeta(location.pathname);
  const isSearchRoute = Boolean(routeMeta);
  const visibleTiles = routeMeta
    ? getTilesByCategory(tiles, routeMeta.key).filter((tile) => matchesSearch(tile, searchValue))
    : tiles.filter((tile) => matchesSearch(tile, searchValue));

  return (
    <div>
      <NavigationHome theme={theme} onToggleTheme={toggleTheme} />
      {isSearchRoute && <SearchBox value={searchValue} onSearch={setSearchValue} />}

      <Routes>
        <Route path="/about-me" element={<Profile className="profile-background" />} />
        <Route path="/azure" element={<CollectionPage tiles={visibleTiles} categoryKey="Azure" />} />
        <Route path="/sharepoint" element={<CollectionPage tiles={visibleTiles} categoryKey="Sharepoint" />} />
        <Route path="/docs" element={<CollectionPage tiles={visibleTiles} categoryKey="Docusaurus" />} />
        <Route path="/content" element={<CollectionPage tiles={visibleTiles} categoryKey="all" />} />
        <Route path="/pdf/:pdfUrl" element={<PdfViewer />} />
        <Route path="/readme/:owner/:repo" element={<ReadmeViewer />} />
        {loading ? (
          <Route path="/" element={<InformativeLoader />} />
        ) : (
          <Route path="/" element={<HomePage tiles={tiles} />} />
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <MainContent />
    </Router>
  );
}

export default App;
